import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { isAxiosError } from 'axios'
import { fetchNotificationChannels, saveNotificationChannels } from './api'
import { changedChannels, initialSelection, selectionError, selectionWarning } from './channels'

/** How the current user is notified for every alarm they're subscribed to.
 * Global per user (not per system or severity): subscriptions decide which
 * alarms, these channels decide how. Saved explicitly, so a "no channel"
 * selection is a deliberate choice the user has seen a warning for. */
export default function NotificationChannelsSection() {
  const queryClient = useQueryClient()
  const channelsQuery = useQuery({ queryKey: ['alarm-notification-channels'], queryFn: fetchNotificationChannels })
  const channels = channelsQuery.data?.channels ?? []

  const [draft, setDraft] = useState<Record<string, boolean>>({})
  const [message, setMessage] = useState<string | null>(null)

  useEffect(() => {
    if (channelsQuery.data) setDraft(initialSelection(channelsQuery.data.channels))
  }, [channelsQuery.data])

  const saveMutation = useMutation({
    mutationFn: () => saveNotificationChannels(changedChannels(channels, draft)),
    onSuccess: (data) => {
      queryClient.setQueryData(['alarm-notification-channels'], data)
      setMessage('Notification preferences saved.')
    },
    onError: (err) => {
      const apiError = isAxiosError(err) ? (err.response?.data as { error?: string } | undefined)?.error : undefined
      setMessage(apiError ?? 'Failed to save notification preferences.')
    },
  })

  if (channelsQuery.isLoading) {
    return (
      <section className="rounded-2xl border border-nav-line bg-card p-5 text-sm text-text-secondary">
        Loading notification channels…
      </section>
    )
  }
  if (channelsQuery.isError || !channelsQuery.data) {
    const apiError = isAxiosError(channelsQuery.error)
      ? (channelsQuery.error.response?.data as { error?: string } | undefined)?.error
      : undefined
    return (
      <section className="rounded-2xl border border-nav-line bg-card p-5 text-sm text-text-secondary">
        {apiError ?? 'Notification channels are unavailable right now.'}
      </section>
    )
  }

  const error = selectionError(channels, draft)
  const warning = selectionWarning(channels, draft)
  const dirty = Object.keys(changedChannels(channels, draft)).length > 0

  return (
    <section className="rounded-2xl border border-nav-line bg-card p-5">
      <h2 className="mb-1 text-base font-semibold text-ink">Notification Channels</h2>
      <p className="mb-3 text-xs text-text-secondary">
        How you're notified about alarms you're subscribed to, on every system.
      </p>

      <div className="space-y-3">
        {channels.map((c) => (
          <label key={c.channel} className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              className="mt-1"
              checked={draft[c.channel] ?? false}
              // An unavailable channel can still be switched off, never on.
              disabled={saveMutation.isPending || (!c.available && !draft[c.channel])}
              onChange={(e) => {
                setMessage(null)
                setDraft((prev) => ({ ...prev, [c.channel]: e.target.checked }))
              }}
            />
            <span>
              <span className="text-text-primary">{c.label}</span>
              {!c.is_explicit && (
                <span className="ml-2 rounded-full bg-bg-tertiary px-2 py-0.5 text-xs text-text-secondary">
                  On by default
                </span>
              )}
              <span className="block text-xs text-text-secondary">{c.note}</span>
            </span>
          </label>
        ))}
      </div>

      {channelsQuery.data.not_selectable.map((c) => (
        <p key={c.channel} className="mt-3 text-xs text-text-secondary">
          {c.label}: {c.note}
        </p>
      ))}

      {error && <p className="mt-3 text-sm text-error">{error}</p>}
      {!error && warning && <p className="mt-3 text-sm text-error">{warning}</p>}
      {message && <p className="mt-3 text-sm text-text-primary">{message}</p>}

      <button
        type="button"
        disabled={!dirty || !!error || saveMutation.isPending}
        onClick={() => saveMutation.mutate()}
        className="mt-3 rounded-full bg-brand px-5 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50"
      >
        Save Channels
      </button>
    </section>
  )
}
